<template>
	<div
		v-if="shouldDisplay && videoSrc"
		:id="blok.block_id || null"
		class="relative"
		:class="`${blok.full_width ? 'full-bleed' : 'main-wrapper'} ${blok.offset || ''} ${blok.visibility ? blok.visibility.join(' ') : ''} ${isFixedHeight ? 'overflow-hidden flex justify-center' : ''}`"
	>
		<div
			class="hero-video overflow-hidden"
			:class="isFixedHeight ? 'hero-max-w-none' : 'w-full'"
			:style="blockHeight"
		>
			<video
				:src="videoSrc"
				type="video/mp4"
				class="w-full hidden lg:block"
				loop
				:controls="blok.controls"
				:muted="blok.muted"
				:autoplay="blok.autoplay"
			/>
			<video
				:src="mobileVideoSrc"
				type="video/mp4"
				class="w-full lg:hidden"
				loop
				:controls="blok.controls"
				:muted="blok.muted"
				:autoplay="blok.autoplay"
			/>
		</div>
		<div
			class="absolute w-full lg:w-full top-0 bottom-0 left-1/2 -translate-x-1/2 z-10 flex flex-col text-white main-wrapper lg:p-7.5"
			:class="`${blok.content_position || 'justify-center items-center'} ${blok.block_id ? 'pt-8 lg:pt-9' : ''}`"
		>
			<div
				class="grid grid-cols-1 gap-4 lg:py-12 py-6 px-6"
				:class="blok.text_align || ''"
			>
				<h2 v-if="blok.heading" :class="blok.heading_styles || ''">
					{{ blok.heading }}
				</h2>
				<h3 v-if="blok.sub_heading" :class="blok.sub_heading_styles || ''">
					{{ blok.sub_heading }}
				</h3>
				<p v-if="blok.text" :class="blok.text_styles || ''">
					{{ blok.text }}
				</p>
				<div
					v-if="blok.call_to_action?.length > 0"
					class="flex flex-col"
					:class="blok.content_position || 'justify-center items-center'"
				>
					<StoryblokComponent
						v-for="call_to_action in blok.call_to_action"
						:key="call_to_action._uid"
						:blok="call_to_action"
					/>
				</div>
				<div
					v-if="blok.countdown_start_date"
					class="flex flex-col"
					:class="blok.content_position || 'justify-center items-center'"
				>
					<CountdownBlock
						:start-date="blok.countdown_start_date"
						wrapper-class=" flex justify-center w-full"
						time-item-class="lg:!text-4xl !text-2xl"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { BlokVideo } from "~/types/storyblok/storyblok";

const HEIGHT_UNITS = {
	pixel: "px",
	percent: "%",
	viewport: "vh"
};

interface Props {
	blok: BlokVideo;
}
const { blok } = defineProps<Props>();

const { shouldDisplay } = useDisplayBlock(blok.start_date, blok.end_date);

const videoSrc = computed(() => blok?.src || blok?.file?.filename);
const mobileVideoSrc = computed(
	() => blok?.src_mobile || blok?.file_mobile?.filename
);
const isFixedHeight = computed(() => blok.height_desktop);

const blockHeight = computed(() => {
	const units = HEIGHT_UNITS[blok.height_units] ?? "px";
	const desktopHeight = blok.height_desktop;
	const mobileHeight = blok.height_mobile || desktopHeight;

	if (!desktopHeight) return {};

	return {
		"--mobile-height": `${mobileHeight}${units}`,
		"--desktop-height": `${desktopHeight}${units}`
	};
});
</script>
